<template>
  <v-card
    border
    class="d-flex flex-column"
  >
    <v-card-title class="d-flex align-center justify-space-between pa-4">
      <h5 class="text-h6 mb-0">{{ $t('column-settings.title') }}</h5>
      <v-btn
        icon="mdi-close"
        variant="text"
        @click="hideColumnSettings"
      />
    </v-card-title>

    <v-list class="dialog-content-overflow">
      <draggable
        v-model="tempColumnSettings"
        handle=".js-draggable"
        ghost-class="ghost-item"
        :animation="200"
        @change="handleDragChange"
      >
        <transition-group>
          <v-list-item
            v-for="col in filteredColumns"
            :key="col.value"
            :ripple="false"
            class="white px-4 column-item"
            density="compact"
            @click.stop="toggleColumnSetting(col.value)"
          >
            <template #prepend>
              <v-checkbox
                :model-value="col.visible"
                color="primary"
                density="compact"
                hide-details
                class="ma-0 pa-0"
                @click.stop="toggleColumnSetting(col.value)"
              />
            </template>

            <v-list-item-title class="ml-3">
              {{ $t(col.text) }}
            </v-list-item-title>

            <template #append>
              <v-icon
                class="js-draggable lead-list__draggable"
                size="small"
              >
                mdi-drag-horizontal-variant
              </v-icon>
            </template>
          </v-list-item>
        </transition-group>
      </draggable>
    </v-list>

    <v-alert
      v-if="noColumnsSelectedError"
      type="error"
      density="compact"
      variant="tonal"
      class="ma-4"
    >
      {{ $t('alerts.column-settings.error') }}
    </v-alert>
  </v-card>
</template>

<script>
import { VueDraggableNext } from 'vue-draggable-next'

import brandingMixin from '@/mixins/branding'

import { getDefaultColumnSettings } from '../configs'

export default {
  components: {
    draggable: VueDraggableNext,
  },

  mixins: [brandingMixin],

  props: {
    columnSettings: {
      type: Array,
      required: true,
      default: () => getDefaultColumnSettings(),
    },
  },

  emits: ['columnSettingsChanged', 'close'],

  data() {
    return {
      tempColumnSettings: [...this.columnSettings],
      noColumnsSelectedError: false,
    }
  },

  computed: {
    filteredColumns() {
      return this.tempColumnSettings.filter((col) => col && !col.hideDisplay)
    },
  },

  watch: {
    columnSettings: {
      handler(value) {
        this.tempColumnSettings = [...value]
      },
      deep: true,
    },
  },

  methods: {
    hideColumnSettings() {
      this.$emit('close')
    },

    handleDragChange(evt) {
      if (evt.moved) {
        // Get the actual indices in tempColumnSettings for the filtered items
        const visibleItems = this.tempColumnSettings.filter((col) => col && !col.hideDisplay)
        const movedItem = visibleItems[evt.moved.oldIndex]
        const actualOldIndex = this.tempColumnSettings.findIndex(
          (item) => item.value === movedItem.value
        )
        const targetItem = visibleItems[evt.moved.newIndex]
        const actualNewIndex = this.tempColumnSettings.findIndex(
          (item) => item.value === targetItem.value
        )

        // Perform the move operation
        const [removed] = this.tempColumnSettings.splice(actualOldIndex, 1)
        this.tempColumnSettings.splice(actualNewIndex, 0, removed)

        this.saveColumnSettings()
      }
    },

    saveColumnSettings() {
      if (this.tempColumnSettings.some((col) => col.visible)) {
        this.noColumnsSelectedError = false
        this.$emit('columnSettingsChanged', [...this.tempColumnSettings])
      } else {
        this.noColumnsSelectedError = true
      }
    },
    isConversion(value) {
      return [
        'conversionCountByType.VALUATION',
        'conversionCountByType.PLOT_VALUATION',
        'conversionCountByType.RETURN_CALL',
        'conversionCountByType.LIFE_ANNUITY',
        'conversionCountByType.PARTIAL_SALE',
        'conversionCountByType.PROPERTY_REQUEST',
        'conversions.download.postalShipping',
      ].includes(value)
    },
    toggleColumnSetting(value) {
      this.tempColumnSettings = this.tempColumnSettings.map((col) => ({
        ...col,
        ...(col.value === value && { visible: !col.visible }),
        ...(col.value === 'conversions' && this.isConversion(value) && { visible: true }),
      }))
      if (!this.noColumnsSelectedError) {
        this.saveColumnSettings()
      }
    },
  },
}
</script>

<style>
.lead-list__draggable {
  cursor: move;
}

.ghost-item {
  opacity: 0.5;
  background: rgba(var(--v-theme-primary), 0.1) !important;
  border: 1px dashed rgb(var(--v-theme-primary));
}

.column-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.column-item.sortable-ghost {
  opacity: 0.5;
  background: rgba(var(--v-theme-primary), 0.1) !important;
  border: 1px dashed rgb(var(--v-theme-primary));
}

.column-item.sortable-drag {
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>

<style scoped>
.dialog-content-overflow {
  max-height: 100%;
  overflow: auto;
}

.text-grey {
  color: rgba(0, 0, 0, 0.54) !important;
}
.due-date {
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
}
</style>
